
// --- Vue & Template imports ---
import { Component, Vue} from 'vue-property-decorator';
import DownloadExcel from 'vue-json-excel/JsonExcel.vue';

// --- Models ---
import { DriverChecklist, ChecklistAction } from '@/models/Driver.model';

// --- Services ---
import AppService from '@/services/app';
import DriverChecklistService from '@/services/driverChecklist';
import DriverService from '@/services/driver';

// --- Third Party ---

@Component({
  components: {
    DownloadExcel,
  },
})
export default class DriverChecklistsListing extends Vue {
   // used by Filter Area
  search: string = '';
  filterOptions: DriverChecklist[] = [];
  checklistFilter: DriverChecklist = {
    id: 0,
    name: '',
  };
  listLoading: boolean = true;

  // used by No Data Slot
  isLoading: boolean = true;
  allActions: ChecklistAction[] = [];
  displayActions: ChecklistAction[] = [];

  // used by Item Dialog for Create
  showItemDialog: boolean = false;
  newListName: string = '';
  valid: boolean = false;
  clicked: boolean = false;

  rules = {
    required: (value: string) => !!value || '',
  };

  // used by Confirm Action Dialog for Action Item
  action: string = '';
  dialogContent: string = '';
  confirmActionDelay: boolean = false;
  showConfirmActionDialog: boolean = false;
  clickedItem: ChecklistAction = {
    checklistId: 0,
    checklistName: '',
    driverId: 0,
    driverNickname: '',
    driverTypeId: 0,
    driverType: '',
  }

  // #region Functions to display list data on page load
    headers() {
      return [
        {
          text: 'Nickname',
          value: 'driverNickname',
        },
        {
          text: 'Type',
          value: 'driverType',
        },
        {
          text: 'Actioned At',
          value: 'actionedAt',
        },
        {
          text: 'Actioned By',
          value: 'actionedBy',
        },
        {
          sortable: false,
          text: '',
          value: 'action',
          align: 'end',
        },
      ];
    }

    async beforeMount() {
      await DriverService.listActive(true);
      await this.listAll();
      await this.listActions();
    }

    async listAll() {
      try {
        this.listLoading = true;
        const response = await DriverChecklistService.listAll();

       if (response && response.data) {
          if (response.data.result && response.data.result === 'false') {
            this.filterOptions = [];
            AppService.errorHandler(response.data.message);
          } else {
            this.filterOptions = response.data;
          }
        } else {
          // response is undefined or has no data field - SHOULD NEVER HAPPEN!
          throw new Error('response: ' + JSON.stringify(response));
        }

      } catch (error) {
        if (error.response) {
          AppService.errorHandler(error.response.statusText);
        } else {
          AppService.logSupportDebug('DriverChecklists.vue - listAll - 356 - ' + error);
          AppService.errorHandler(this.$store.getters['app/messages'].couldNotConnect);
        }

      } finally {
        this.listLoading = false;
      }
    }

    async listActions() {
      try {
        this.isLoading = true;
        const response = await DriverChecklistService.listActions();

       if (response && response.data) {
          if (response.data.result && response.data.result === 'false') {
            this.allActions = [];
            AppService.errorHandler(response.data.message);
          } else {
            this.allActions = response.data;
          }
        } else {
          // response is undefined or has no data field - SHOULD NEVER HAPPEN!
          throw new Error('response: ' + JSON.stringify(response));
        }

      } catch (error) {
        if (error.response) {
          AppService.errorHandler(error.response.statusText);
        } else {
          AppService.logSupportDebug('DriverChecklists.vue - listActions - 386 - ' + error);
          AppService.errorHandler(this.$store.getters['app/messages'].couldNotConnect);
        }

      } finally {
        this.isLoading = false;
      }
    }

    filterDrivers() {
      this.displayActions = this.allActions.filter((item: ChecklistAction) => item.checklistId === this.checklistFilter.id);
    }

    allowAddNewFunctionality() {
      return this.$store.getters['app/allowAccess'](this.$store.getters['users/loggedInUser'], [
        { type: 'Super User', level: 2},
        { type: 'Operator', level: 2},
      ]);
    }

    allowDoneFunctionality() {
      return this.$store.getters['app/allowAccess'](this.$store.getters['users/loggedInUser'], [
        { type: 'Super User', level: 2},
        { type: 'Operator', level: 2},
      ]);
    }
  // #endregion

  // #region Functions required for the CSV Download 
    getDataForCSV() {
      return this.displayActions;
    }

    getFieldsForCSV() {
      return {
        'Driver': 'driverNickname',
        'Type': 'driverType',
        'Actioned At': 'actionedAt',
        'Actioned By': 'actionedBy',
      };
    }
  // #endregion

  // #region Functions required for the Item Dialog for Create/Update
    async launchNewItemDialog() {
      this.newListName = '';

      this.valid = false;

      if (this.$refs.itemForm) {
        (this.$refs.itemForm as any).resetValidation();
      }

      this.showItemDialog = true;
    }

    async doCreate() {
      try {
        this.clicked = true;

        const response = await DriverChecklistService.create({         
          name: this.newListName,
        });

        if (response && response.data) {
          if (response.data.result && response.data.result === 'false') {
            AppService.errorHandler(response.data.message);
          } else {
            AppService.successHandler(response.data.message);
            await this.listAll();
            await this.listActions();
            const index:number = this.filterOptions.findIndex((item: DriverChecklist) => ('' + item.id) === response.data.checklistId);
            if (index >= 0) {
              // #notworried we should always get the relevant id back from a successful service call and since we refreshed the list above it will be there
              this.checklistFilter = this.filterOptions[index];
            }
            
            this.filterDrivers();
            this.showItemDialog = false;
          }
        } else {
          // response is undefined or has no data field - SHOULD NEVER HAPPEN!
          throw new Error('response: ' + JSON.stringify(response));
        }

      } catch (error) {
        if (error.response) {
          AppService.errorHandler(error.response.statusText);
        } else {
          AppService.logSupportDebug('DriverChecklists.vue - doCreate - 475 - ' + error);
          AppService.errorHandler(this.$store.getters['app/messages'].couldNotConnect);
        }

      } finally {
        this.clicked = false;
      }
    }
  // #endregion

  // #region Functions required for the Confirm Action Dialog for Done
    launchDoneItemDialog(item: ChecklistAction) {
      this.clickedItem = item;

      this.action = 'DONE';
      this.dialogContent = 'You are about to mark the Driver named <span style="font-weight: 700">' 
        + item.driverNickname + '</span> as <span style="font-weight: 700;color: green">DONE</span> on the '
        + '<span style="font-weight: 700">' + item.checklistName + '</span> Checklist.<br/><br/> Are you sure?';

      this.showConfirmActionDialog = true;
    }

    ConfirmActionDialog_cancel() {
      this.showConfirmActionDialog = false;
    }

    async ConfirmActionDialog_doConfirmed() {
      if (this.action === 'DONE') {
        await this.doDoneAction();
      }
    }

    async doDoneAction() {
      try {
        this.confirmActionDelay = true;

        const response = await DriverChecklistService.done({
          checklistId: this.clickedItem.checklistId,
          driverId: this.clickedItem.driverId,
        });

        if (response && response.data) {
          if (response.data.result && response.data.result === 'false') {
            AppService.errorHandler(response.data.message);
          } else {
            AppService.successHandler(response.data.message);
            await this.listActions();
            this.filterDrivers();
            this.showConfirmActionDialog = false;
          }
        } else {
          // response is undefined or has no data field - SHOULD NEVER HAPPEN!
          throw new Error('response: ' + JSON.stringify(response));
        }

      } catch (error) {
        if (error.response) {
          AppService.errorHandler(error.response.statusText);
        } else {
          AppService.logSupportDebug('DriverChecklists.vue - doDoneAction - 534 - ' + error);
          AppService.errorHandler(this.$store.getters['app/messages'].couldNotConnect);
        }

      } finally {
        this.confirmActionDelay = false;
      }
    }
  // #endregion

}
